/**
 * NotificationsInbox — the full list behind the bell's "See all" link
 * (notifications part two).
 *
 * /t/:tenantSlug/notifications. Any signed-in member sees their own
 * rows only (server-scoped); there is no capability gate beyond
 * membership, matching the API's `read`-gated, own-rows-only routes.
 *
 * PageShell's standard content-width container (the same band every
 * other top-level list page opens on) and a subtitle, rather than the
 * bare form-width shell — a notice list is content, not a form. "Mark
 * all read" hides when the current view's list is empty; nothing to
 * mark is nothing to offer.
 */

import { useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import {
  useNotificationsList,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  notificationHref,
} from '@/lib/notifications';
import type { NotificationWire } from '@/lib/api';
import { PageShell } from '@/components/primitives/PageShell';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { TAB_RAIL_CLASSES } from '@/components/primitives/tabRail';

const PAGE_SIZE = 25;

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function NotificationsInbox(): ReactNode {
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>();
  const tenantSlug = routeTenantSlug ?? '';
  const { whoami } = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const unreadOnly = searchParams.get('unread') === '1';
  const offset = Math.max(0, Number.parseInt(searchParams.get('offset') ?? '0', 10) || 0);

  const { rows, isLoading, isError, error } = useNotificationsList(tenantSlug, {
    unreadOnly,
    limit: PAGE_SIZE,
    offset,
    enabled: whoami !== null,
  });
  const markRead = useMarkNotificationRead(tenantSlug);
  const markAllRead = useMarkAllNotificationsRead(tenantSlug);

  const [justMarkedAll, setJustMarkedAll] = useState(false);

  function setUnreadOnly(next: boolean): void {
    const sp = new URLSearchParams(searchParams);
    if (next) sp.set('unread', '1');
    else sp.delete('unread');
    sp.delete('offset');
    setSearchParams(sp, { replace: true });
  }

  function goToOffset(next: number): void {
    const sp = new URLSearchParams(searchParams);
    if (next > 0) sp.set('offset', String(next));
    else sp.delete('offset');
    setSearchParams(sp, { replace: true });
  }

  function openRow(row: NotificationWire): void {
    if (!row.read_at) markRead.mutate(row.id);
    navigate(notificationHref(tenantSlug, row.link));
  }

  const isEmpty = !isLoading && !isError && rows.length === 0;

  return (
    <PageShell
      title="Notifications"
      maxWidth="xl"
      testId="notifications-page"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div role="tablist" aria-label="Filter" className={`flex gap-1 ${TAB_RAIL_CLASSES.tablist}`}>
          {(
            [
              { key: false, label: 'All' },
              { key: true, label: 'Unread' },
            ] as const
          ).map((opt) => {
            const isActive = unreadOnly === opt.key;
            return (
              <button
                key={String(opt.key)}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setUnreadOnly(opt.key)}
                className={
                  `${TAB_RAIL_CLASSES.tab} ` +
                  (isActive
                    ? `border-warm-400 ${TAB_RAIL_CLASSES.active}`
                    : TAB_RAIL_CLASSES.inactive)
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {!isEmpty && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="inbox-mark-all-read"
            onClick={() => {
              markAllRead.mutate();
              setJustMarkedAll(true);
            }}
          >
            Mark all read
          </Button>
        )}
      </div>

      <AsyncBoundary
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={isEmpty}
        emptyHeadline={
          unreadOnly
            ? 'Nothing unread'
            : justMarkedAll
              ? 'All caught up'
              : "You'll see notices here"
        }
        emptyBody={
          unreadOnly
            ? "You're all caught up."
            : "When something needs you, you'll see it here. You're not missing anything."
        }
      >
        <ul className="divide-y divide-sage-800/[8%] border border-sage-800/[8%] rounded-card overflow-hidden">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                data-testid="inbox-row"
                onClick={() => openRow(row)}
                className={
                  'block w-full text-left px-4 py-3 hover:bg-sage-800/[4%] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                  (row.read_at ? 'bg-paper' : 'bg-warm-50')
                }
              >
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm font-medium text-ink">{row.title}</p>
                  <p className="shrink-0 text-xs text-ink-soft">{formatTimestamp(row.created_at)}</p>
                </div>
                <p className="mt-0.5 text-sm text-ink-soft">{row.summary}</p>
                {!row.read_at && (
                  <span className="mt-1 inline-block text-xs font-semibold text-warm-600">Unread</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </AsyncBoundary>

      {!isLoading && !isError && (rows.length === PAGE_SIZE || offset > 0) && (
        <div className="flex items-center justify-between mt-4">
          <Button
            variant="ghost"
            size="sm"
            disabled={offset === 0}
            onClick={() => goToOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={rows.length < PAGE_SIZE}
            onClick={() => goToOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      )}
    </PageShell>
  );
}
